import { cloneElement, CSSProperties } from 'react';
import { cx } from '@linaria/atomic';
import { motion, AnimatePresence } from 'motion/react';
import { useMatchMedia } from '@frontend/responsiveness';
import { theme } from '@frontend/theme';
import { mergeClasses } from '../../../helpers/merge-classes';
import { ContentLoader } from '../../loader/content-loader';
import type { ModalBoxProps } from '../atoms/modal-box';
import { modalBox, modalBoxFlex, modalBoxScroll } from '../atoms/modal-box.styles';
import { useModalContext } from '../provider/modal.provider';
import { useResponsiveModal } from './responsive-modal.context';
import { responsiveModalBoxMobile } from './responsive-modal.styles';

const maxWidthIncludingMargins = '100vw - 48px';

/**
 * Equivalent to ModalBox when !isResponsiveMode
 * Otherwise, equivalent to full screen / mobile.
 */
export const ResponsiveModalBox = ({
  children,
  asChild,
  className,
  flex,
  minWidth = 0,
  maxWidth = 0,
  style,
  ...rest
}: ModalBoxProps) => {
  const { containerProps, loadingState } = useModalContext();
  const { isResponsiveMode } = useResponsiveModal();

  const isAboveMaxWidth = useMatchMedia({ minWidth: maxWidth + 48 });
  const widthValue = maxWidth ? (isAboveMaxWidth ? 'auto' : '100%') : 'unset';

  const desktopWidthStyle: CSSProperties = {
    width: widthValue,
    minWidth: minWidth > 0 ? `min(${minWidth}px, ${maxWidthIncludingMargins})` : 0,
    maxWidth: maxWidth ? `min(${maxWidth}px, ${maxWidthIncludingMargins})` : `calc(${maxWidthIncludingMargins})`,
  };

  const boxClassName = isResponsiveMode ? responsiveModalBoxMobile : cx(modalBox, flex ? modalBoxFlex : modalBoxScroll);

  const props = {
    className: mergeClasses(boxClassName, 'wv-modal-box', className),
    onClick: (e) => e.stopPropagation(),
    ...rest,
    style: isResponsiveMode ? style : { ...desktopWidthStyle, ...style },
    ...containerProps,
  };

  if (asChild) {
    const element = cloneElement(children as React.ReactElement, props);

    if (typeof element.type === 'string') {
      const Component = motion[element.type];
      return (
        <AnimatePresence>
          <Component
            {...element.props}
            className={mergeClasses('responsive-modal__box responsive-modal__box--element', element.props.className)}
          />
        </AnimatePresence>
      );
    }

    return <>{element}</>;
  }

  return (
    <AnimatePresence>
      <motion.section {...props} className={mergeClasses('responsive-modal__box', props.className)}>
        {children}
        <ContentLoader style={{ borderRadius: theme.borderRadius.medium }} {...loadingState} />
      </motion.section>
    </AnimatePresence>
  );
};
